import { useState, useRef, useEffect } from "react";
import { Mail, Send, CheckCircle2, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");
  const mountedAt = useRef(Date.now());

  useEffect(() => { document.title = "Contact Us · Help Solve The Case"; }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending"); setError("");
    try {
      await api.post("/public/contact", { ...form, elapsed_ms: Date.now() - mountedAt.current });
      setStatus("sent");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-charcoal text-white flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="inline-flex items-center gap-2 text-gold mb-3">
          <Mail className="w-5 h-5" /> <span className="label-mono text-xs">Contact Us</span>
        </div>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight">Get in touch.</h1>
        <p className="mt-3 text-white/60 max-w-xl">
          Questions, feedback, or a case suggestion? Send us a note and we'll get back to you by email.
        </p>

        {status === "sent" ? (
          <div className="mt-10 card-surface rounded-lg p-8 text-center" data-testid="contact-success">
            <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-gold" />
            </div>
            <div className="font-display font-bold text-xl">Message sent</div>
            <p className="mt-2 text-white/55">Thanks for reaching out — we'll reply to the email you provided.</p>
            <button onClick={() => setStatus("idle")} data-testid="contact-send-another"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-white/15 text-white/80 hover:border-gold/50 hover:text-gold transition-colors text-sm">
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 card-surface rounded-lg p-6 sm:p-8 space-y-5" data-testid="contact-form">
            {/* Honeypot — hidden from humans; bots fill it and get silently dropped */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
              <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-white/50 mb-1.5 label-mono">Name</label>
                <input required maxLength={100} value={form.name} onChange={set("name")} data-testid="contact-name"
                  className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-white placeholder-white/30 focus:border-gold outline-none" placeholder="Your name" />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1.5 label-mono">Email</label>
                <input required type="email" maxLength={200} value={form.email} onChange={set("email")} data-testid="contact-email"
                  className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-white placeholder-white/30 focus:border-gold outline-none" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1.5 label-mono">Subject <span className="text-white/30">(optional)</span></label>
              <input maxLength={150} value={form.subject} onChange={set("subject")} data-testid="contact-subject"
                className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-white placeholder-white/30 focus:border-gold outline-none" placeholder="What's this about?" />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1.5 label-mono">Message</label>
              <textarea required rows={6} maxLength={4000} value={form.message} onChange={set("message")} data-testid="contact-message"
                className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-white placeholder-white/30 focus:border-gold outline-none resize-y" placeholder="How can we help?" />
            </div>

            {status === "error" && <p className="text-sm text-red-400" data-testid="contact-error">{error}</p>}

            <button type="submit" disabled={status === "sending"} data-testid="contact-submit"
              className="inline-flex items-center justify-center gap-2 rounded-sm font-semibold text-sm px-5 py-3 bg-gold text-charcoal hover:bg-gold-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {status === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Message</>}
            </button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
